import {
  getCurrencyExponent,
  type SupportedCurrencyCode,
} from "@repo/api-schema";

// Bounded by the API's currency allowlist, so this won't grow without bound.
// If we ever add per-locale variants this becomes a leak — convert to an LRU
// at that point.
const CURRENCY_FORMATTERS = new Map<SupportedCurrencyCode, Intl.NumberFormat>();

function formatterFor(currency: SupportedCurrencyCode): Intl.NumberFormat {
  let f = CURRENCY_FORMATTERS.get(currency);
  if (!f) {
    const exponent = getCurrencyExponent(currency);
    // Pin fraction-digits to the currency's ISO 4217 minor exponent so
    // 0-decimal currencies (JPY, KRW…) and 3-decimal ones (KWD, BHD…)
    // render with the right precision regardless of the runtime locale's
    // default for the code.
    f = new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      minimumFractionDigits: exponent,
      maximumFractionDigits: exponent,
    });
    CURRENCY_FORMATTERS.set(currency, f);
  }
  return f;
}

/**
 * Formats a minor-unit integer amount as a localized currency string. The
 * string form is what `<MoneyAmount>` renders; call this directly when the
 * amount has to be composed into a longer label (an `aria-label`, a sentence).
 */
export function formatMoney(
  minor: number,
  currency: SupportedCurrencyCode,
): string {
  return formatterFor(currency).format(
    minor / 10 ** getCurrencyExponent(currency),
  );
}

/**
 * Parse a user-entered major-unit amount ("12.34") into integer minor units
 * (cents/pence) for the given currency exponent, rounding half-up on the digit
 * after the cut. Returns `NaN` for empty or non-numeric input. Kept BigInt-free
 * (amounts here are far below `Number.MAX_SAFE_INTEGER`) and standalone so the
 * settlement form doesn't depend on the heavy expense form's internals.
 */
export function amountToMinor(
  amount: string,
  currency: SupportedCurrencyCode,
): number {
  const exponent = getCurrencyExponent(currency);
  const trimmed = amount.trim();
  if (trimmed === "") return Number.NaN;
  if (!/^-?\d+(\.\d+)?$/.test(trimmed)) {
    const major = Number(trimmed);
    if (!Number.isFinite(major)) return Number.NaN;
    return Math.round(major * 10 ** exponent);
  }
  const negative = trimmed.startsWith("-");
  const digits = negative ? trimmed.slice(1) : trimmed;
  const [intPart, fracPart = ""] = digits.split(".");
  const padded = (fracPart + "0".repeat(exponent)).slice(0, exponent);
  const cutDigit = (fracPart[exponent] ?? "0").charCodeAt(0) - 48;
  const base = Number((intPart ?? "0") + padded);
  if (!Number.isFinite(base)) return Number.NaN;
  const rounded = base + (cutDigit >= 5 ? 1 : 0);
  return negative ? -rounded : rounded;
}

/** The `<input type="number">` step for a currency (e.g. "0.01", or "1" for JPY). */
export function minorStepFor(currency: SupportedCurrencyCode): string {
  const exponent = getCurrencyExponent(currency);
  return exponent === 0 ? "1" : `0.${"0".repeat(exponent - 1)}1`;
}
